import { useEffect, useRef } from 'react';

// Lector de códigos USB o Bluetooth.
//
// No hace falta driver ni librería: TODOS los lectores del mercado se
// comportan como teclados. Teclean los dígitos del código y mandan un
// Enter. Lo único que hay que resolver es distinguir esa ráfaga de una
// persona escribiendo, y eso se hace por velocidad: un lector emite el
// código completo en 20-60 ms, y nadie teclea trece dígitos en ese tiempo.
//
// Por qué escuchar el documento entero y no un campo con foco: en un
// mostrador o en un pasillo, quien escanea tiene las manos ocupadas y no
// va a hacer clic en una caja de texto primero. La pantalla debe reaccionar
// al escaneo venga cuando venga.
//
// props:
//   onCodigo(codigo, {medio})  se llama con el código completo y, de
//                      segundo argumento (U-02 fase b), por dónde llegó:
//                      siempre `{medio: 'LECTOR'}`, porque este gancho
//                      solo dispara con una ráfaga. Quien lo llamaba con
//                      un solo argumento sigue recibiendo el código igual.
//   activo             permite apagarlo (por ejemplo, con un modal abierto)
//   minimo             longitud mínima para considerarlo un código (4)
export const MS_ENTRE_TECLAS = 35;   // por encima de esto, es una persona escribiendo
const MS_LIMPIEZA = 200;      // ráfaga abandonada: se descarta el buffer

export function useEscanerTeclado(onCodigo, { activo = true, minimo = 4 } = {}) {
  const buffer = useRef('');
  const ultima = useRef(0);
  const temporizador = useRef(null);
  const callback = useRef(onCodigo);

  // El callback se guarda en una referencia para que cambiarlo no
  // reinstale el escuchador en cada render.
  useEffect(() => { callback.current = onCodigo; }, [onCodigo]);

  useEffect(() => {
    if (!activo) return undefined;

    function alTeclear(e) {
      // Un campo de contraseña nunca debe recibir esto, y un textarea
      // tampoco: ahí la persona está escribiendo de verdad.
      const destino = e.target;
      if (destino instanceof HTMLElement) {
        const etiqueta = destino.tagName;
        if (etiqueta === 'TEXTAREA' || destino.type === 'password') return;
      }

      const ahora = Date.now();
      const rapido = ahora - ultima.current < MS_ENTRE_TECLAS;
      ultima.current = ahora;

      if (e.key === 'Enter') {
        const codigo = buffer.current;
        buffer.current = '';
        if (codigo.length >= minimo) {
          e.preventDefault();
          callback.current?.(codigo, { medio: 'LECTOR' });
        }
        return;
      }

      // Solo caracteres imprimibles de uno en uno (las teclas especiales
      // llegan como 'Shift', 'Tab'…).
      if (e.key.length !== 1) return;

      buffer.current = rapido || buffer.current === ''
        ? buffer.current + e.key
        : e.key;

      clearTimeout(temporizador.current);
      temporizador.current = setTimeout(() => { buffer.current = ''; }, MS_LIMPIEZA);
    }

    window.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('keydown', alTeclear);
      clearTimeout(temporizador.current);
    };
  }, [activo, minimo]);
}
